import { http } from '@pendo/http';
import { RECENTLY_SELECTED_HISTORY_TYPES } from '@/vuex/modules/user-settings/history/history.constants';
import { isCrossApp } from '@/common/cross-app-countables/cross-app-countables.utils';
import { formatCrossAppDisplayName } from '@/vuex/modules/apps/apps.utils.js';

export function fetchFeatures () {
    return http.get('api/s/_SID_/feature?expand=*');
}

export function fetchFeature (featureId) {
    return http.get(`api/s/_SID_/feature/${featureId}`);
}

export function fetchFeaturesIgnoredFrustrations () {
    return http.get('api/s/_SID_/feature/ignorefrustration');
}

export function fetchFeatureGuides (featureId) {
    return http.get(`api/s/_SID_/feature/${featureId}/guide`);
}

export function updateFeature (feature, shouldStartProcessing) {
    return http.put(`api/s/_SID_/feature/${feature.id}`, feature).then(async (updatedFeature) => {
        if (shouldStartProcessing) {
            try {
                await startProcessingFeature(feature.id);
            } catch (error) {
                const reverseProcessingJobIsAlreadyRunning = error.response.status === 409;
                if (!reverseProcessingJobIsAlreadyRunning) {
                    throw error;
                }
            }
        }

        return updatedFeature;
    });
}

export function updateFeatureIgnoredFrustrations ({ id, ignoredFrustrationTypes }) {
    return http.put(`api/s/_SID_/feature/${id}/ignorefrustration`, { ignoredFrustrationTypes });
}

export function createFeature (newFeature, shouldStartProcessing = true) {
    return http.post('api/s/_SID_/feature', newFeature).then((savedFeatureResponse) => {
        const savedFeature = savedFeatureResponse.data;
        if (shouldStartProcessing) {
            startProcessingFeature(savedFeature.id);
        }

        return savedFeatureResponse;
    });
}

export function deleteFeature (featureId) {
    return http.delete(`api/s/_SID_/feature/${featureId}`);
}

export function startProcessingFeature (featureId) {
    return http.post(`api/s/_SID_/feature/${featureId}/apply`, null);
}

export function shapeAnalyticsFeature (feature, { isStarred, getIconByAppId }) {
    return Object.freeze({
        id: feature.id,
        name: feature.name,
        group: feature.group,
        appId: feature.appId,
        appIds: feature.appIds,
        appIcon: getIconByAppId(feature.appId),
        kind: feature.kind,
        dirty: feature.dirty,
        isCoreEvent: feature.isCoreEvent,
        isStarred: !!isStarred(feature.id),
        icon: feature.icon || { type: 'feature' },
        eventPropertyConfigurations: feature.eventPropertyConfigurations,
        eventPropertyNameList: feature.eventPropertyNameList,
    });
}

export function buildRecentFeatures (state, rootState, rootGetters, { excludeCrossApp }) {
    const isStarred = rootGetters['stars/getStarByItemId'];

    return rootGetters['userSettings/history/itemHistory'](RECENTLY_SELECTED_HISTORY_TYPES.FEATURE_RECENTLY_SELECTED)
        .filter(({ id }) => state.map[id])
        .filter(({ id }) => !excludeCrossApp || !isCrossApp(state.map[id]))
        .map(({ id, timestamp }) => {
            const feature = state.map[id];

            return {
                id: feature.id,
                name: feature.name,
                group: feature.group,
                appId: feature.appId,
                appIds: feature.appIds,
                appIcon: rootGetters['apps/getIconByAppId'](feature.appId),
                appDisplayName: formatCrossAppDisplayName(feature, rootState.apps.map),
                kind: feature.kind,
                dirty: feature.dirty,
                isCoreEvent: feature.isCoreEvent,
                isStarred: !!isStarred(feature.id),
                icon: feature.icon || { type: 'feature' },
                eventPropertyConfigurations: feature.eventPropertyConfigurations,
                eventPropertyNameList: feature.eventPropertyNameList,
                lastUsed: timestamp,
            };
        });
}
